import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import { VehicleDetailsModal } from "../../modals/VehicleDetailsModal";
import Pagination from "../../ui/Pagination";
import { Truck } from "../../../types";
import { useVehicleData } from "../../../hooks";
import VehicleTableHeader from "./VehicleTableHeader";
import VehicleTableRow from "./VehicleTableRow";
import VehicleTableControls from "./VehicleTableControls";

interface CollapsibleTableProps {
  trucks: Truck[];
  index: number;
  token?: string;
  deleteTruck: (truck: Truck) => void;
  updateTruck: (truck: Truck) => void;
  updateEntryDirection: (truck: Truck, direction: "in" | "out") => void;
  updateLicensePlate?: (truck: Truck, newLicensePlate: string) => void;
  availabilityFilter?: "all" | "available" | "unavailable";
}

function CollapsibleTable({
  trucks,
  index,
  token,
  deleteTruck,
  updateTruck,
  updateEntryDirection,
  updateLicensePlate,
  availabilityFilter = "all",
}: CollapsibleTableProps): React.JSX.Element {
  const [sortBy, setSortBy] = useState<"time" | "alphabetical">("time");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);

  // Use custom hook for data processing
  const groupedTrucks = useVehicleData({
    trucks,
    availabilityFilter,
    sortBy,
    searchTerm,
  });

  // Restore scroll position
  useEffect(() => {
    const savedScrollPosition = localStorage.getItem("scrollPosition");
    if (savedScrollPosition) {
      window.scrollTo(0, parseInt(savedScrollPosition, 10));
      localStorage.removeItem("scrollPosition");
    }
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [availabilityFilter, searchTerm, sortBy]);

  const handleDetailsClick = (registration: string) => {
    setSelectedRegistration(registration);
    setDetailsModalOpen(true);
  };

  // Pagination logic
  const totalItems = Object.keys(groupedTrucks).length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedEntries = Object.entries(groupedTrucks).slice(
    startIndex,
    endIndex
  );

  return (
    <Box>
      <VehicleTableControls
        sortBy={sortBy}
        onSortChange={setSortBy}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
        <Table aria-label="vehicle table">
          <VehicleTableHeader />
          <TableBody>
            {paginatedEntries.map(([registration, trucks]) => (
              <VehicleTableRow
                key={registration}
                registration={registration}
                trucks={trucks}
                token={token}
                onDetailsClick={handleDetailsClick}
                onDirectionChange={updateEntryDirection}
                onLicensePlateChange={updateLicensePlate}
                onDelete={deleteTruck}
                onUpdateTruck={updateTruck}
                groupedTrucks={groupedTrucks}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[6, 12, 24, 48]}
        />
      )}

      <VehicleDetailsModal
        trucks={groupedTrucks[selectedRegistration] || []}
        registration={selectedRegistration}
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        token={token}
        updateEntryDirection={updateEntryDirection}
        updateTruck={updateTruck}
        updateLicensePlate={updateLicensePlate}
        deleteTruck={deleteTruck}
        groupedTrucks={groupedTrucks}
      />
    </Box>
  );
}

export default CollapsibleTable;
